/**
 * Client-side validation.
 *
 * These rules deliberately mirror src/utils/schemas.js on the API. The server
 * is the authority — this layer exists so a visitor sees the problem next to
 * the field instead of waiting for a round trip.
 */

export const isEmail = (v) =>
  /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(String(v || '').trim());

/** Indian mobile numbers, plus a permissive international fallback. */
export const isPhone = (v) => {
  const digits = String(v || '').replace(/[\s\-()]/g, '');
  return /^(\+?91|0)?[6-9]\d{9}$/.test(digits) || /^\+?[1-9]\d{7,14}$/.test(digits);
};

export const isUrl = (v) => {
  const s = String(v || '').trim();
  if (!s) return true; // empty is fine; use required() to demand a value
  return /^(https?:\/\/)?[\w-]+(\.[\w-]+)+([/?#][^\s]*)?$/i.test(s);
};

export const isName = (v) => /^[\p{L}\p{M}\s.'-]{2,}$/u.test(String(v || '').trim());

// -----------------------------------------------------------------------------
// Rule builders. Each returns null when valid, or a message when not.
// -----------------------------------------------------------------------------
export const required = (message = 'This field is required') => (v) => {
  if (Array.isArray(v)) return v.length ? null : message;
  if (typeof v === 'boolean') return v ? null : message;
  return String(v ?? '').trim() ? null : message;
};

export const minLength = (n, message) => (v) =>
  !v || String(v).trim().length >= n
    ? null
    : message || `Use at least ${n} characters`;

export const maxLength = (n, message) => (v) =>
  !v || String(v).trim().length <= n
    ? null
    : message || `Keep this under ${n} characters`;

export const email = (message = 'Enter a valid email address') => (v) =>
  !v || isEmail(v) ? null : message;

export const phone = (message = 'Enter a valid 10-digit mobile number') => (v) =>
  !v || isPhone(v) ? null : message;

export const url = (message = 'Enter a valid link, for example https://example.com') => (v) =>
  isUrl(v) ? null : message;

export const name = (message = 'Enter a valid name') => (v) =>
  !v || isName(v) ? null : message;

export const numberBetween = (min, max, message) => (v) => {
  if (v === '' || v == null) return null;
  const n = Number(v);
  if (Number.isNaN(n)) return message || 'Enter a number';
  return n >= min && n <= max ? null : message || `Enter a value between ${min} and ${max}`;
};

export const fileSize = (maxMb, message) => (file) =>
  !file || file.size <= maxMb * 1024 * 1024
    ? null
    : message || `That file is larger than ${maxMb} MB`;

export const fileType = (extensions, message) => (file) => {
  if (!file) return null;
  const ext = `.${file.name.split('.').pop().toLowerCase()}`;
  return extensions.includes(ext)
    ? null
    : message || `Upload a ${extensions.join(', ')} file`;
};

/** Runs rules in order and returns the first message, or null. */
export const runRules = (value, rules = []) => {
  for (const rule of rules) {
    const message = rule(value);
    if (message) return message;
  }
  return null;
};

/** Validates a whole values object against a { field: [rules] } map. */
export const validateAll = (values, schema) => {
  const errors = {};
  for (const [field, rules] of Object.entries(schema)) {
    const message = runRules(values[field], rules);
    if (message) errors[field] = message;
  }
  return errors;
};
